import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  IconButton,
  TableContainer,
  Paper,
  CircularProgress,
  Pagination,
  Menu, MenuItem
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { useTranslation } from "react-i18next";
import { showUnderDevelopment } from "../../../../components/alerts";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import emptyscreen from "../../../../assets/Dashboard/emptyscreen.svg";
import AttendanceDetailsDrawer from "./DetailsDrawer";
import TruncatedCell from "../../../../components/TruncatedCell";
import StatusDropdown from "../../../../components/StatusDropdown";
import CustomButton from "../../../../components/CustomButton";
import {
  convertLocalDateTimeToUTC,
  convertUTCToLocalDateTime,
} from "../../../../components/dateTimeUtils";
import {
  getClientTimeZoneFromServerPayload,
  getPunchDisplayTimezone,
  isUtcLikeTimezone,
} from "../../../../components/attendanceScheduleDisplay";
const ROWS_PER_PAGE = 5;

const AttendanceTable = ({  onRecordsChange, onRowSelect }) => {
  const [records, setRecords] = useState([]);
  const [todayContext, setTodayContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;


  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setLoading(true);
        const todayLocal = dayjs().format("YYYY-MM-DD");
        const dateParam =
          convertLocalDateTimeToUTC(`${todayLocal}T00:00:00`) || todayLocal;
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Omit `tz` so the server uses the same default as Postman (company DB / resolver),
        // then format the UI using the timezone echoed in `data.timezone` (matches scheduleStatus math).
        const res = await fetch(
          `${BASE_URL}/attendance/today?date=${encodeURIComponent(dateParam)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json();
        console.log("Fetched attendance data:", data);
        if (!data.error && data.data?.records) {
          setRecords(data.data.records);
          const fromApi = getClientTimeZoneFromServerPayload(data.data);
          const apiTz =
            data.data?.timezone && String(data.data.timezone).trim() !== ""
              ? String(data.data.timezone).trim()
              : null;
          const viewerTz = apiTz || browserTz;
          const schedulePayload = data.data?.schedule ?? data.data?.details?.schedule;
          setTodayContext({
            ...data.data,
            break_time_minutes:
              data.data?.break_time_minutes ?? schedulePayload?.takenBreakMinutes,
            viewerResolvedTimeZone: viewerTz,
            clientTimeZone:
              fromApi && !isUtcLikeTimezone(fromApi) ? fromApi : viewerTz,
          });
          onRecordsChange?.(data.data.records);
          setPage(1);
        } else {
          setRecords([]);
          setTodayContext(null);
          onRecordsChange?.([]);
        }
      } catch (err) {
        console.error("Error fetching attendance:", err);
        setRecords([]);
        setTodayContext(null);
        onRecordsChange?.([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchAttendance();
  }, [token]);

  const HaederStyles = {
    color: "#44546F",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    whiteSpace: "nowrap",
    px: "16px",
    py: "10px",
  };
  const DataStyles = {
    color: "#172B4D",
    fontSize: "12px",
    fontFamily: "Poppins, sans-serif",
    fontWeight: 400,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    px: "16px",
    py: "10px",
  };
  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    setDrawerOpen(true);
    setAnchorEl(null);
  };
  // pagination calculations
  const totalPages = Math.ceil(records.length / ROWS_PER_PAGE);
  const startIndex = (page - 1) * ROWS_PER_PAGE;
  const paginatedRecords = records.slice(startIndex, startIndex + ROWS_PER_PAGE);
const mapAttendanceReviewStatus = (reviewStatus) => {
  if (!reviewStatus) return reviewStatus;
  const normalizedStatus = String(reviewStatus).trim().toLowerCase().replace(/\s+/g, "_");

  if (normalizedStatus === "pending") return "pendingPunch";
  if (["approved", "validated", "valid"].includes(normalizedStatus)) return "validatedPunch";
  if (["rejected", "invalid", "has_issue", "has_issues"].includes(normalizedStatus)) {
    return "invalidPunch";
  }

  return reviewStatus;
};

/** Badge key for StatusDropdown: schedule classification, else action type. */
const getPunchActivityStatus = (row) => {
  const classification =
    row?.scheduleStatus?.classification ?? row?.schedule_status?.classification;
  if (classification != null && String(classification).trim() !== "") {
    return String(classification).trim();
  }
  return row?.actionType ?? row?.action_type ?? null;
};

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 3,
        border: "1px solid #E4E6EF",
        p: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 2,
          pb: 2,
          borderBottom: "2px solid #091E4224",
        }}
      >
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: { xs: 16, sm: 20 },
            fontFamily: "Nunito Sans, sans-serif",
            fontWeight: 600,
            color: "##363333",
            textAlign: "left",
            flex: "1 1 auto",
            minWidth: 0,
          }}
        >
          {t("attendanceTable.title")}
        </CustomText>

        {/* Date Picker */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjs()}
            disabled
            format="YYYY-MM-DD"
            maxDate={dayjs()}
        
            slots={{
              openPickerIcon: () => null,     // remove calendar
              inputAdornment: () => null,     // remove icon wrapper
            }}
        
            slotProps={{
              textField: {
                size: "small",
                InputProps: {
                  readOnly: true,
                  sx: {
                    padding: 1,
                    height: 30,                 // 🚀 SMALL HEIGHT
                    fontSize: "16px",           // 🚀 SMALL FONT
                  },
                },
                sx: {
                  flexShrink: 0,
                  width: 130,
                  "& .MuiInputBase-root": {
                    height: 30,                 // input height
                    fontSize: "13px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6 6px !important", // small padding
                    fontSize: "13px",            // small font
                  },
                },
              },
        
              // Disable calendar popup
              popper: { sx: { display: "none" } },
              openPickerButton: { sx: { display: "none" } },
            }}
          />
        </LocalizationProvider>
   
      </Box>

      {/* Loading */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : records.length === 0 ? (
        // Empty State
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: 300,
            justifyContent: "center",
          }}
        >
          <img src={emptyscreen} alt="No Data" style={{ width: 200 }} />
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: "16px",
              color: "#003149",
              pt: 1,
            }}
          >
            {t("empty.no_user")}
          </CustomText>
        </Box>
      ) : (
        <>
          {/* Table */}
          <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HaederStyles, minWidth: 150 }}>
                    {t("attendanceTable.columns.lastActivity")}
                  </TableCell>
                  <TableCell sx={{ ...HaederStyles, minWidth: 160 }}>
                    {t("attendanceTable.columns.time")}
                  </TableCell>
                  <TableCell sx={{ ...HaederStyles, minWidth: 180 }}>
                    {t("attendanceTable.columns.location")}
                  </TableCell>
                  <TableCell sx={{ ...HaederStyles, minWidth: 150 }}>
  {t("attendanceTable.columns.reviewStatus")}
</TableCell>
                  {/* <TableCell sx={{ ...HaederStyles, py: "6px", px: "0px" }}>
                    {t("attendanceTable.columns.image")}
                  </TableCell> */}
                  <TableCell
                    align="center"
                    sx={{ ...HaederStyles, minWidth: 100 }}
                  >
                    {t("attendanceTable.columns.action")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((row, index) => {
                  const activityStatus = getPunchActivityStatus(row);
                  return (
                  <TableRow key={index}
                   hover
  sx={{ cursor: "pointer" }}
  onClick={() => onRowSelect?.(row)}>
                    <TableCell sx={{ ...DataStyles, minWidth: 150 }}>
                      {activityStatus ? (
                        <Box sx={{ pointerEvents: "none", display: "inline-flex" }}>
                          <StatusDropdown currentStatus={activityStatus} />
                        </Box>
                      ) : (
                        "--"
                      )}
                    </TableCell>
                    <TableCell sx={{ ...DataStyles, minWidth: 160 }}>
                      {(() => {
                        const formattedValue = convertUTCToLocalDateTime(
                          row.occurred_at ?? row.occurredAt,
                          {
                            timezone: getPunchDisplayTimezone(todayContext),
                          }
                        );
                        return formattedValue === "—" ? "--" : formattedValue;
                      })()}
                    </TableCell>
                    <TableCell sx={{ ...DataStyles, minWidth: 180, maxWidth: 220 }}>
                      {/* {row.locationData?.textDescription || "No location"} */}
                      <TruncatedCell text={row.locationData?.textDescription || "No location"} />
                    </TableCell>

<TableCell sx={DataStyles}>
  {/* {row.validation?.reviewStatus || "--"}
   */}
   <Box sx={{ pointerEvents: "none" }}>
                         <StatusDropdown
                           currentStatus={
                             mapAttendanceReviewStatus(
                               row.validation?.reviewStatus || row.validation?.review_status || "--"
                             )
                           }
                         />
                       </Box>
</TableCell>

                
                    <TableCell align="center">

  <CustomButton size='small' label=   {t("common.view")}  sx={{py:0.3}}   onClick={(e) => {
    e.stopPropagation();
    setSelectedRecord(row);
    setDrawerOpen(true);
  }} />
</TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              shape="rounded"
              color="primary"
              size="small"
            />
          </Box>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleView} sx={{ fontSize: "12px" }}>
              {t("monthlySummary.menu.view")}
            </MenuItem>
          </Menu>
          <AttendanceDetailsDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            record={selectedRecord}
            todayContext={todayContext}
          />

        </>
      )}
    </Box>
  );
};

export default AttendanceTable;




